import React from "react";
import PropTypes from "prop-types";
import { useTranslation, Trans } from "react-i18next";
import {
  BarChart,
  Bar,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getShortDate } from "../../../services/dateService";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";

const StyledToolTipContent = styled("div")`
  margin: 0px;
  padding: 10px;
  background-color: rgb(255, 255, 255);
  border: 1px solid rgb(204, 204, 204);
  white-space: nowrap;
  .MuiTypography-body2 {
    font-weight:300;
  }
`;

const CustomTooltip = ({ payload, active }) => {
  if (active) {
    let data = payload[0].payload;
    let difference;
    if (data.diff < 0) {
      difference = (
        <Trans
          i18nKey={"components.waterfall.tooltipDecreasing"}
          values={{
            val: Math.round(Math.abs(data.diff) * 100) / 100,
          }}
        ></Trans>
      );
    } else if (data.diff === 0) {
      difference = (
        <Trans i18nKey={"components.waterfall.tooltipMaintaining"}></Trans>
      );
    } else {
      difference = (
        <Trans
          i18nKey={"components.waterfall.tooltipIncreasing"}
          values={{
            val: Math.round(Math.abs(data.diff) * 100) / 100,
          }}
        ></Trans>
      );
    }

    return (
      <StyledToolTipContent>
        <Typography pb={2} variant="body2">
          {data.referenceDate}
        </Typography>
        <Typography pb={1} variant="body2">
          <Trans
            i18nKey={"components.waterfall.tooltipCurrentText"}
            values={{
              percentage: Math.abs(data.current),
            }}
          ></Trans>
        </Typography>
        <Typography variant="body2">{difference}</Typography>
      </StyledToolTipContent>
    );
  }

  return null;
};

const Waterfall = (props) => {
  const { t, i18n } = useTranslation();
  let { demands } = props.data;
  const {
    width,
    height,
    margin,
    legendHeight,
    legendAlign,
    xAxis,
    bars,
    chartName,
  } = props;

  const toPercent = (decimal) => {
    return t("components.waterfall.value", { val: decimal });
  };

  demands = demands.map((val) => {
    let diff = val.current - val.previous;

    return {
      referenceDate: getShortDate(val.referenceDate, i18n.language),
      previous: +val.previous.toFixed(2),
      current: +val.current.toFixed(2),
      diff: +diff.toFixed(2),
    };
  });
  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        key={chartName}
        width={width}
        height={height}
        data={demands}
        margin={margin}
      >
        <CartesianGrid key="grid" strokeDasharray="3 3" />
        <XAxis key="xAxis" dataKey={xAxis} />
        <YAxis
          type="number"
          domain={[
            (dataMin) => 0,
            (dataMax) =>
              dataMax < 5
                ? 5
                : dataMax > 50
                  ? 100
                  : Math.ceil(dataMax / 10) * 10,
          ]}
          tickFormatter={toPercent}
        />
        {bars.map((bar, index) => {
          return (
            <React.Fragment key={index}>
              <Tooltip content={CustomTooltip} />
              <Legend
                verticalAlign={legendAlign}
                height={legendHeight}
                payload={[
                  {
                    value: t("components.waterfall.legendIncreasing"),
                    color: bar.fill,
                  },
                  {
                    value: t("components.waterfall.legendDecreasing"),
                    color: bar.negativeFill,
                  },
                  {
                    value: t("components.waterfall.legendMaintaining"),
                    color: bar.maintainingFill,
                  },
                ]}
              />
              <Bar
                key={index + "transparent"}
                dataKey={bar.transparent}
                stackId={bar.stackId}
                fill="transparent"
              ></Bar>
              <Bar
                key={index}
                dataKey={bar.dataKey}
                stackId={bar.stackId}
                fill={bar.fill}
              >
                {demands.map((item, InsideIndex) => {
                  if (item.diff < 0) {
                    return <Cell key={InsideIndex} fill={bar.negativeFill} />;
                  } else if (item.diff === 0) {
                    return (
                      <Cell
                        height={1}
                        key={InsideIndex}
                        fill={bar.maintainingFill}
                      />
                    );
                  }
                  return <Cell key={InsideIndex} fill={bar.fill} />;
                })}
              </Bar>
            </React.Fragment>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};
Waterfall.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  hasLegend: PropTypes.bool,
  legendHeight: PropTypes.number,
  legendAlign: PropTypes.string,
  xAxis: PropTypes.string,
  yAxis: PropTypes.string,
  bars: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string,
      stackId: PropTypes.string,
      fill: PropTypes.string,
      transparent: PropTypes.string,
      negativeFill: PropTypes.string,
      maintainingFill: PropTypes.string,
    })
  ),
  tooltip: PropTypes.element,
  chartName: PropTypes.string,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
  }),
  data: PropTypes.shape({
    demands: PropTypes.arrayOf(
      PropTypes.shape({
        referenceDate: PropTypes.string,
        percentage: PropTypes.number,
      })
    ),
  }),
};
export default Waterfall;
